import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/** Shown before an article that others link to is unpublished, archived or
 *  deleted. A count alone doesn't help — this names the articles, so the editor
 *  can go and fix the sentences rather than quietly leaving dead ends. */
export function KbBacklinkWarning({ slug, action = "unpublish", open, onOpenChange, onConfirm }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !slug) return;
    setData(null);
    api.get(`/kb/articles/${slug}/backlinks`)
      .then((r) => setData(r.data))
      .catch(() => setData({ failed: true, count: 0, articles: [] }));
  }, [open, slug]);

  const verb = { unpublish: "Unpublish", archive: "Archive", delete: "Delete" }[action] || "Continue";
  const effect = {
    unpublish: "Readers following those links will land on a page they can't see.",
    archive: "Readers following those links will land on a page they can't see.",
    delete: "Those links will break — permanently.",
  }[action];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="kb-backlink-warning">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {!data ? "Checking what links here…"
              : data.failed ? "We couldn't check what links here"
              : `${data.count} article${data.count === 1 ? "" : "s"} point${data.count === 1 ? "s" : ""} here`}
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="kb-backlink-warning-effect">
              {data.failed
                ? "The backlink check didn't answer, so we can't tell you what points here. Carry on only if you're sure."
                : effect}
            </p>
            <ul className="max-h-64 space-y-1 overflow-y-auto" data-testid="kb-backlink-list">
              {data.articles.map((a) => (
                <li key={a.slug} className="flex items-center justify-between gap-3 rounded-md border border-slate-100 dark:border-slate-800 px-3 py-2"
                  data-testid={`kb-backlink-${a.slug}`}>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">{a.name}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      {a.related_only ? "listed as a related article"
                        : `${a.links} link${a.links === 1 ? "" : "s"}`}
                      {a.draft && " · draft"}{a.archived && " · archived"}
                    </span>
                  </span>
                  <Link to={`/kb/a/${a.slug}/edit`} target="_blank" rel="noreferrer"
                    data-testid={`kb-backlink-fix-${a.slug}`}
                    className="flex shrink-0 items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-800 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-primary/40 hover:text-primary">
                    <ExternalLink className="h-3 w-3" /> Fix
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
            data-testid="kb-backlink-cancel">Leave it alone</Button>
          <Button size="sm" onClick={() => { onOpenChange(false); onConfirm?.(); }}
            data-testid="kb-backlink-confirm"
            className={action === "delete" ? "bg-rose-600 text-white hover:bg-rose-700" : ""}>
            {verb} anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
